import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Socket } from 'socket.io-client'

import { RootState } from 'dataStore/state'
import OrdersService from 'dataStore/service/orders.service'
import { createAdminOrdersSocket } from 'dataStore/service/ordersRealtime.service'
import { buildAdminOrdersListQueryFromState } from 'handlers/buildAdminOrdersListParams'
import { createNewOrderNotifyDedupe } from 'handlers/newOrderNotifyDedupe'
import { processIncomingNewOrder } from 'handlers/processIncomingNewOrder'
import { processIncomingPaymentStatus } from 'handlers/processIncomingPaymentStatus'
import {
  OrderPaymentStatusPayload,
} from 'handlers/formatOrderPaymentNotificationText'
import {
  acquireScreenWakeLock,
  WakeLockSentinel,
} from 'handlers/requestWakeLock'
import { bindAudioUnlockOnFirstGesture } from 'handlers/unlockAudioOnGesture'
import { syncAdminPushPreference } from 'handlers/syncAdminPushPreference'
import { areAdminNewOrderNotificationsEnabled } from 'handlers/adminNewOrderNotificationsPreference'
import { useActions } from 'hooks/useActions'

/** Интервал fallback poll latest-заказа */
const POLL_INTERVAL_MS = 20_000

/**
 * Payload события `order:created` с сервера.
 */
export type OrderCreatedSocketPayload = {
  id: number
  pizzeria_id: number
  total_price: number
  create_at: string
}

/**
 * Слушает новые заказы (socket + poll), играет звук и показывает Notification.
 * Подключать только внутри авторизованного MainLayout.
 */
export const useNewOrderNotifications = (): void => {
  const navigate = useNavigate()
  const location = useLocation()
  const { getAdminOrdersList, getDashboardSummary } = useActions()

  const filter = useSelector((state: RootState) => state.orders.filter)
  const pagination = useSelector((state: RootState) => state.orders.pagination)
  const sortBy = useSelector((state: RootState) => state.orders.sortBy)
  const sortOrder = useSelector((state: RootState) => state.orders.sortOrder)
  const filterPizzeriaId = filter?.pizzeria_id ?? null

  const locationPathnameRef = useRef(location.pathname)
  locationPathnameRef.current = location.pathname

  const ordersStateRef = useRef({ filter, pagination, sortBy, sortOrder })
  ordersStateRef.current = { filter, pagination, sortBy, sortOrder }

  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const getAdminOrdersListRef = useRef(getAdminOrdersList)
  getAdminOrdersListRef.current = getAdminOrdersList

  const getDashboardSummaryRef = useRef(getDashboardSummary)
  getDashboardSummaryRef.current = getDashboardSummary

  const dedupeRef = useRef(createNewOrderNotifyDedupe())
  const firstPollRef = useRef(true)
  const pollGenerationRef = useRef(0)
  const socketRef = useRef<Socket | null>(null)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)

  // Смена фильтра синхронно в render — до любого in-flight poll
  const previousFilterRef = useRef(filterPizzeriaId)
  const filterPizzeriaIdRef = useRef(filterPizzeriaId)
  if (previousFilterRef.current !== filterPizzeriaId) {
    previousFilterRef.current = filterPizzeriaId
    firstPollRef.current = true
    pollGenerationRef.current += 1
  }
  filterPizzeriaIdRef.current = filterPizzeriaId

  useEffect(() => {
    const token = localStorage.getItem('token')
    const dedupe = dedupeRef.current

    /**
     * Refetch списка и сводки только на главной (`/`).
     */
    const refetchHomeIfNeeded = (): void => {
      if (locationPathnameRef.current !== '/') {
        return
      }

      const {
        filter: currentFilter,
        pagination: currentPagination,
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
      } = ordersStateRef.current

      getAdminOrdersListRef.current(
        buildAdminOrdersListQueryFromState({
          filter: currentFilter,
          pagination: currentPagination,
          sortBy: currentSortBy,
          sortOrder: currentSortOrder,
        })
      )
      getDashboardSummaryRef.current({
        pizzeria_id: currentFilter?.pizzeria_id,
        status_id: currentFilter?.status_id,
        payment_type: currentFilter?.paymentType,
        payment_status: currentFilter?.paymentStatus,
      })
    }

    /**
     * Обработчик события сокета `order:created`.
     * @param payload — данные нового заказа
     */
    const handleIncoming = (payload: OrderCreatedSocketPayload): void => {
      if (payload?.id == null || payload?.pizzeria_id == null) {
        return
      }

      processIncomingNewOrder({
        orderId: payload.id,
        orderPizzeriaId: payload.pizzeria_id,
        totalPrice: Number(payload.total_price) || 0,
        filterPizzeriaId: filterPizzeriaIdRef.current,
        dedupe,
        onNavigateHome: () => navigateRef.current('/'),
        onRefetchHome: refetchHomeIfNeeded,
      })
    }

    /**
     * Обработчик `order:payment_status` — оплата/отмена от платёжной системы.
     * @param payload — данные события оплаты
     */
    const handlePaymentStatus = (
      payload: OrderPaymentStatusPayload
    ): void => {
      if (
        payload?.id == null ||
        payload?.pizzeria_id == null ||
        !payload?.outcome
      ) {
        return
      }

      processIncomingPaymentStatus({
        payload,
        filterPizzeriaId: filterPizzeriaIdRef.current,
        onNavigateHome: () => navigateRef.current('/'),
        onRefetchHome: refetchHomeIfNeeded,
      })
    }

    /**
     * Fallback poll: первый вызов только запоминает latestId.
     */
    const pollLatest = async (): Promise<void> => {
      const pollGeneration = pollGenerationRef.current
      const pizzeriaIdForRequest = filterPizzeriaIdRef.current

      try {
        const { latestId, latestTotalPrice } =
          await OrdersService.getAdminOrdersLatest(pizzeriaIdForRequest)

        // Фильтр сменился во время запроса — ответ устарел
        if (pollGeneration !== pollGenerationRef.current) {
          return
        }

        if (latestId == null) {
          return
        }

        if (firstPollRef.current) {
          dedupe.setSeenLatestId(latestId)
          firstPollRef.current = false
          return
        }

        const seenLatestId = dedupe.getSeenLatestId()

        if (latestId <= seenLatestId) {
          return
        }

        if (dedupe.has(latestId)) {
          dedupe.setSeenLatestId(latestId)
          return
        }

        // Poll уже отфильтрован по pizzeria_id
        processIncomingNewOrder({
          orderId: latestId,
          orderPizzeriaId: pizzeriaIdForRequest ?? 0,
          totalPrice: latestTotalPrice ?? 0,
          filterPizzeriaId: pizzeriaIdForRequest,
          dedupe,
          onNavigateHome: () => navigateRef.current('/'),
          onRefetchHome: refetchHomeIfNeeded,
        })

        // Если фильтр отсёк — всё равно двигаем seen, чтобы не крутить тот же id
        if (!dedupe.has(latestId)) {
          dedupe.setSeenLatestId(latestId)
        }
      } catch {
        // poll не критичен для работы админки
      }
    }

    /**
     * Запрашивает / обновляет Screen Wake Lock.
     */
    const refreshWakeLock = async (): Promise<void> => {
      const sentinel = await acquireScreenWakeLock()
      wakeLockRef.current = sentinel
    }

    /**
     * При возврате на вкладку — Wake Lock и reconnect сокета.
     */
    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return
      }

      void refreshWakeLock()

      const socket = socketRef.current
      if (socket && !socket.connected) {
        socket.connect()
      }
    }

    if (token) {
      const socket = createAdminOrdersSocket(token)
      socketRef.current = socket
      socket.on('order:created', handleIncoming)
      socket.on('order:payment_status', handlePaymentStatus)
    }

    void refreshWakeLock()
    void pollLatest()
    const pollTimerId = window.setInterval(() => {
      void pollLatest()
    }, POLL_INTERVAL_MS)

    const unbindAudioUnlock = bindAudioUnlockOnFirstGesture()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Permission на мобилках запрашиваем только по клику кнопки уведомлений
    // (без жеста ОС часто не показывает диалог, а повторный запрос уже бесполезен)

    return () => {
      window.clearInterval(pollTimerId)
      unbindAudioUnlock()
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      const socket = socketRef.current
      if (socket) {
        socket.off('order:created', handleIncoming)
        socket.off('order:payment_status', handlePaymentStatus)
        socket.disconnect()
        socketRef.current = null
      }

      const wakeLock = wakeLockRef.current
      if (wakeLock && !wakeLock.released) {
        void wakeLock.release()
      }
      wakeLockRef.current = null
    }
  }, [])

  // Синхронизация preferred-фильтра push при смене пиццерии на главной
  useEffect(() => {
    if (!areAdminNewOrderNotificationsEnabled()) {
      return
    }
    const timeoutId = window.setTimeout(() => {
      void syncAdminPushPreference(filterPizzeriaId)
    }, 300)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [filterPizzeriaId])
}
